import _ from "lodash";

import { BothIds } from "./BothIds";
import { ShoppingListItem } from "../datatypes/article/ShoppingListItem";
import { PageType } from "../datatypes/PageType";
import { SortCriteria } from "../datatypes/ShoppingList";
import { GdprStorage } from "../uidispatcherapi/GdprStorage";

const REORDER = "reorder";
const HISTORY_LIST = "HISTORY";

const userListPageTypes = ["SAVED_CART", "HISTORY_PAGE", "REORDER", "SHOPPING_LIST"];

const STORAGE_KEY_LIST_SORT = "LISTPAGE_LAST_USED_SORTING";

type SortCriteriaData = {
    CRITERIA: SortCriteria;
    I18N_KEY: string;
    TEST_CSS_CLASS: string;
    DESCRIPTION: string;
    VARIANT_BASED: boolean;
};

export default {
    isAListPage(pageType: PageType): boolean {
        return userListPageTypes.includes(pageType);
    },

    filterItemsByIds(items: { [x: string]: any }[], filterIds: string[]): { [x: string]: any }[] {
        const filterIdsSet = new Set(filterIds);
        return _.compact(_.filter(items, item => item && filterIdsSet.has(item.articleVariantId)));
    },

    getSortCriteriaForSearch(sortCriteria: string | null | undefined) {
        if (sortCriteria === "asc" || sortCriteria === "desc") {
            return sortCriteria;
        }
        return "asc";
    },

    getBundles(items: { [x: string]: any }[], querySearchItemIds?: string[] | null | undefined): string[] {
        const filteredItems = querySearchItemIds ? this.filterItemsByIds(items, querySearchItemIds) : items;
        const results = _.map(filteredItems, listItem => {
            if (listItem.bettyBundleId) {
                return listItem.bettyBundleId;
            } else {
                // FIXME: we should make sure that all list items have a bettyBundleId - then this concatenation should
                // be removed
                return listItem.articleVariantId + listItem.bundleId;
            }
        });
        return results;
    },

    orderItemsByIdOrder(items: Array<{ [x: string]: any }>, itemIdsInOrder: BothIds[]): Array<{ [x: string]: any }> {
        const mapped = _.map(itemIdsInOrder, (itemIds: BothIds) => {
            return _.find(items, item => {
                if (!item) {
                    return false;
                }
                // compare by bundle if possible (it's more precise):
                if (itemIds.bettyBundleId) {
                    return item.bettyBundleId === itemIds.bettyBundleId;
                }
                // otherwise compare by variant:
                return item.articleVariantId === itemIds.variantId;
            });
        });
        return _.compact(mapped);
    },

    // A dirty little hack to make flow happy, as the id field is set for carts, the listId for actual lists.
    // This is only necessary because in the RenameList and DeleteList it can be either a cart or a list.
    getId(list: { [x: string]: any }) {
        return list.id || list.listId;
    },

    displayCategoryView(sort: string) {
        return sort === this.sortCategory;
    },

    hasPreviousUnmergedBundleId(item: { [x: string]: any }) {
        if (item.hasOwnProperty("followUpHistoryItems")) {
            return _.some(item.followUpHistoryItems, ["merged", false]);
        }
        return false;
    },

    getPreviousUnmergedBundleId(item: { [x: string]: any }) {
        if (this.hasPreviousUnmergedBundleId(item)) {
            const historyElement = _.find(item.followUpHistoryItems, ["merged", false]);
            return (historyElement || {}).previousBettyBundleId;
        }
        return undefined;
    },

    getPreviousUnmergedBundle(item: { [x: string]: any }) {
        if (this.hasPreviousUnmergedBundleId(item)) {
            return _.find(item.followUpHistoryItems, ["merged", false]);
        }
        return undefined;
    },

    getPreviousUnmergedBundleIdForVariant(
        itemList: { [x: string]: any }[],
        variantId: string
    ): string | null | undefined {
        const itemFromList = _.find(itemList, ["articleVariantId", variantId]);
        if (typeof itemFromList !== "undefined") {
            return this.getPreviousUnmergedBundleId(itemFromList);
        }
        return undefined;
    },

    sortAndDuplicate(bothIds: BothIds[], itemsOrdered: string[]): BothIds[] {
        const withDuplicatesListOfLists: BothIds[][] = _.map(itemsOrdered, variantId => {
            const matchingItems: BothIds[] = _.filter(bothIds, item => {
                return item.variantId === variantId;
            });
            const itemsDuped: BothIds[] = _.map(matchingItems, matchingItem => {
                return { variantId, bettyBundleId: matchingItem.bettyBundleId };
            });
            return itemsDuped;
        });
        const flattened: BothIds[] = _.flatten(withDuplicatesListOfLists);
        return flattened;
    },

    sortWithoutDuplication(bothIds: BothIds[], itemsOrdered: string[]): BothIds[] {
        let bothIds2 = _.clone(bothIds);
        const result: Array<BothIds | null | undefined> = _.map(itemsOrdered, itemId => {
            const found = _.find(bothIds2, (item: BothIds) => {
                if (!item) {
                    return false;
                }
                return item.variantId === itemId;
            });
            if (found) {
                bothIds2 = bothIds2.filter((x: BothIds) => {
                    return x.variantId !== found.variantId || x.bettyBundleId !== found.bettyBundleId;
                });
            }
            return found;
        });
        return _.compact(result);
    },

    adaptItemsOrderedForBundles(
        shoppingListItems: ShoppingListItem[],
        itemsOrdered: string[],
        needsDuplication: boolean
    ): BothIds[] {
        const bothIds: BothIds[] = _.map(shoppingListItems, item => {
            const variantId = item.articleVariantId;
            const bettyBundleId = item.bettyBundleId;
            return { variantId, bettyBundleId };
        });
        if (needsDuplication) {
            return this.sortAndDuplicate(bothIds, itemsOrdered);
        }
        return this.sortWithoutDuplication(bothIds, itemsOrdered);
    },

    sortCategory: "byCategory",

    sortAscending: "ascending",

    sortDescending: "descending",

    sortLexicographicalAZ: "lexicographicalAZ",

    sortLexicographicalZA: "lexicographicalZA",

    personalListOld: "PERSONAL",

    personalList: "personal list",

    storeList: "STORE",

    reorderList: REORDER,

    savedCartList: "savedCart",

    historyList: HISTORY_LIST,

    SORT_CRITERIA: {
        byCategory: {
            CRITERIA: "byCategory",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_CATEGORY",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-category",
            DESCRIPTION: "category",
            VARIANT_BASED: true
        },
        ascending: {
            CRITERIA: "ascending",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_ASC",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-ascending",
            DESCRIPTION: "date_added_asc",
            VARIANT_BASED: false
        },
        descending: {
            CRITERIA: "descending",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_DESC",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-descending",
            DESCRIPTION: "date_added_desc",
            VARIANT_BASED: false
        },
        asc: {
            CRITERIA: "asc",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_AZ",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-a-to-z",
            DESCRIPTION: "a_to_z",
            VARIANT_BASED: true
        },
        desc: {
            CRITERIA: "desc",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_ZA",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-z-to-a",
            DESCRIPTION: "z_to_a",
            VARIANT_BASED: true
        }
    } as { [x: string]: SortCriteriaData },

    isVariantBased(sortCriteria: string): boolean | null | undefined {
        const found = _.find(this.SORT_CRITERIA, s => {
            return s.CRITERIA === sortCriteria;
        });
        return found ? found.VARIANT_BASED : undefined;
    },

    shouldDisableBundleSelector(listType: string | void): true | void {
        return listType && (listType === this.savedCartList || listType === this.storeList) ? true : undefined;
    },

    readSortCriteria(): string | null | undefined {
        return GdprStorage.localStorage.get(STORAGE_KEY_LIST_SORT).actualResult;
    },

    writeSortCriteria(sort: string) {
        GdprStorage.localStorage.put(STORAGE_KEY_LIST_SORT, sort, GdprStorage.FUNCTIONAL);
    },

    getSelectedSortCriteria(eventKey: string | null | undefined): SortCriteriaData {
        if (eventKey) {
            return this.SORT_CRITERIA[eventKey];
        }
        const fromStorage = this.readSortCriteria();
        if (fromStorage && this.SORT_CRITERIA[fromStorage]) {
            return this.SORT_CRITERIA[fromStorage];
        }
        return this.SORT_CRITERIA.byCategory;
    }
};
